package org.zest.insan_360_website.data.page_states.dashboard

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.browser.storage.setItem
import com.varabyte.kobweb.core.PageContext
import kotlinx.browser.localStorage
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.zest.insan_360_website.components.widgets.ToastState
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.LanguageSettings
import org.zest.insan_360_website.data.page_states.AuthState
import org.zest.insan_360_website.data.page_states.CreditState.creditState
import org.zest.insan_360_website.data.page_states.UserPreferences
import org.zest.insan_360_website.model.*
import org.zest.insan_360_website.pages.Answer
import org.zest.insan_360_website.pages.USER_ANSWERS_KEY
import org.zest.insan_360_website.pages.user.main_layout.GrantState
import org.zest.insan_360_website.utils.NetworkUtils
import org.zest.insan_360_website.utils.processResponse

object MainDashPageState {
    var supAdmDataStats by mutableStateOf<SupAdmDataStatsPayload?>(null)
    var grantState by mutableStateOf(GrantState())
    var adminDataStats by mutableStateOf<AdminDataStatsPayload?>(null)
    var personalCreditHistory by mutableStateOf<CreditsHistoryPayload?>(null)
    var isLoading by mutableStateOf(true)
    var showVideoModal by mutableStateOf(false)

    fun reset() {
        supAdmDataStats = null
        grantState = GrantState()
        adminDataStats = null
        personalCreditHistory = null
        isLoading = true
        showVideoModal = false
    }

    fun fetchAdminDashboardInfo(scope: CoroutineScope) {
        val role = AuthState.role
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        if (role != UserRole.ADMIN) {
            return
        }
        isLoading = true

        scope.launch {
            try {
                val request = AdminRequest(
                    email = email,
                    role = role,
                    request = AdminMainDashRequest(
                        email = email,
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/admin/dashboard/stats", request)
                    ?: throw Exception(
                        LanguageSettings.ResponseError.session_expired[UserPreferences.language]
                            ?: "Session expired, please log in again."
                    )

                adminDataStats = processResponse(response, fallbackErrorLog = "Error fetching admin data")

            } catch (e: Exception) {
                console.error("Network error fetching admin stats: ${e.message}") // LANGUAGE: localized for Malay, for now toast only
                adminDataStats = null
            } finally {
                isLoading = false
            }
        }
    }

    fun fetchSupAdmDashboardInfo(scope: CoroutineScope) {
        val role = AuthState.role
        val email = AuthState.email ?: return
        if (AuthState.token == null) return

        if (role != UserRole.SUPER_ADMIN) {
            return
        }
        isLoading = true

        scope.launch {
            try {
                val request = SuperAdminRequest<Unit>(
                    email = email,
                    role = role
                )
                val response = NetworkUtils.postSecureApi("/api/super-admin/dashboard/stats", request)
                    ?: throw Exception(
                        LanguageSettings.ResponseError.session_expired[UserPreferences.language]
                            ?: "Session expired, please log in again."
                    )

                supAdmDataStats = processResponse(response)

            } catch (e: Exception) {
                console.error("Network error fetching super admin stats: ${e.message}")
                supAdmDataStats = null
            } finally {
                isLoading = false
            }
        }
    }

    fun handleGrantCredit(scope: CoroutineScope, toast: ToastState, onSuccess: () -> Unit) {
        val email = AuthState.email ?: return
        if (AuthState.token == null) return
        val role = AuthState.role

        if (role != UserRole.SUPER_ADMIN) return


        grantState = grantState.copy(isProcessing = true)

        scope.launch {
            try {
                val request = SuperAdminRequest(
                    email = email,
                    role = role,
                    request = SupAdmGrantCreditRequest(
                        grantorEmail = email,
                        granteeEmail = grantState.email,
                        count = grantState.count
                    )
                )
                val response = NetworkUtils.postSecureApi("/api/super-admin/grant-credit", request)
                    ?: throw Exception(
                        LanguageSettings.ResponseError.session_expired[UserPreferences.language]
                            ?: "Session expired, please log in again."
                    )

                processResponse(response, toast, "Error granting credit") {
                    onSuccess()
                }

            } catch (e: Exception) {
                val errorText = LanguageSettings.ResponseError.MainDash.grant_credit[UserPreferences.language]
                    ?: "Error granting credit"
                console.error("$errorText: ${e.message}")
                toast.show("$errorText: ${e.message}", ToastType.ERROR)
            } finally {
                grantState = grantState.copy(isProcessing = false)
            }
        }
    }

    suspend fun fetchPersonalCreditHistory() {

        isLoading = true

        try {
            val response = NetworkUtils.getSecureApi("/api/credit/history")
                ?: throw Exception(
                    LanguageSettings.ResponseError.session_expired[UserPreferences.language]
                        ?: "Session expired, please log in again."
                )
            personalCreditHistory = processResponse(response, fallbackErrorLog = "Error fetching credit history")
        } catch (e: Exception) {
            console.log("Error fetching credit history: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    fun handleStartAssessmentClick(toast: ToastState, ctx: PageContext) {
        when (creditState) {
            CreditStatus.ACTIVE -> {
                showVideoModal = true
            }

            CreditStatus.IN_PROGRESS -> {
                toast.show(
                    LanguageSettings.AssessmentPage.continuing[UserPreferences.language]
                        ?: "Continuing Test"
                )
                ctx.router.navigateTo("/assessment", com.varabyte.kobweb.navigation.UpdateHistoryMode.REPLACE)
            }

            else -> {
                toast.show(
                    LanguageSettings.UserDashboardPage.need_active_credit[UserPreferences.language]
                        ?: "Need an active credit to start the assessment",
                    ToastType.ERROR
                )
            }
        }
    }

    suspend fun consumeCreditAndProceed(toast: ToastState, ctx: PageContext) {
        val errorText = LanguageSettings.ResponseError.MainDash.start_test[UserPreferences.language]
            ?: "Error when starting assessment"
        if (creditState == CreditStatus.ACTIVE) {
            val email = AuthState.email ?: return
            if (AuthState.token == null) return

            toast.show(
                LanguageSettings.AssessmentPage.starting[UserPreferences.language]
                    ?: "Starting Assessment",
                ToastType.INFO
            )

            try {
                val request = ChangeCreditStatusRequest(
                    email = email,
                    changeTo = CreditStatus.IN_PROGRESS
                )
                val response = NetworkUtils.patchSecureApi("/api/credit/change-status", request)
                    ?: throw Exception(
                        LanguageSettings.ResponseError.session_expired[UserPreferences.language]
                            ?: "Session expired, please log in again."
                    )

                processResponse<Unit>(response, toast, "Unable to start assessment") {
                    ctx.router.navigateTo("/assessment", com.varabyte.kobweb.navigation.UpdateHistoryMode.REPLACE)
                    localStorage.setItem(USER_ANSWERS_KEY, List(24) { Answer(null,null) })
                    toast.show(
                        LanguageSettings.AssessmentPage.has_started[UserPreferences.language]
                            ?: "Assessment has been started",
                        ToastType.SUCCESS)
                }

            } catch (e: Exception) {
                toast.show(e.message ?: "$errorText: ${e.message}", ToastType.ERROR)
                console.error("$errorText: ${e.message}")
            }
        } else {
            toast.show("$errorText: credit state misalignment $creditState", ToastType.ERROR)
        }
    }
}